<template>
  <nav :class="['navbar', { 'shrink': isShrunk }]">
    <div class="logo-container">
      <img src="@/assets/RTH Group Logo Image.png" alt="RTH Cleaners" />
      <span class="brand-text">RTH Group</span>
    </div>

    <div class="menu-icon" @click="toggleMenu">
      <span v-if="!menuOpen">&#9776;</span>
      <span v-else>&#10005;</span>
    </div>

    <div class="nav-links" :class="{ 'active': menuOpen }">
      <router-link to="/" @click="closeMenu">Home</router-link>
      <router-link to="/apply" @click="closeMenu">Application Form</router-link>
      <router-link to="/about" @click="closeMenu">About</router-link>
      <router-link to="/paymentprocessing" @click="closeMenu"> Make Payment</router-link>
      <router-link to="/contact" @click="closeMenu">Contact</router-link>

      <!-- Unauthenticated -->
      <template v-if="!user">
        <router-link to="/login" @click="closeMenu">Login</router-link>
      </template>

      <!-- Authenticated -->
      <template v-else>
        <!-- Applicant link -->
        <router-link
          v-if="profile?.role === 'user'"
          to="/my-applications"
          @click="closeMenu"
        >
          My Applications
        </router-link>

        <router-link
          v-if="(profile?.role === 'user' ) && isEmployee"
          to="/my-paystubs"
          @click="closeMenu"
        >
          My Pay Stubs
        </router-link>

        <!-- Admin link -->
        <router-link
          v-if="profile?.role === 'admin'"
          to="/admin"
          @click="closeMenu"
        >
          Admin
        </router-link>

        <button class="logout-button" @click="handleLogout">
          Logout
        </button>
      </template>
    </div>
  </nav>
</template>

<script setup>
import { notifyError } from '@/services/notify'
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter }                  from 'vue-router'
import { auth, db }                   from '@/firebase'
import { onAuthStateChanged, signOut } from 'firebase/auth'

import { collection, query, where, getDocs } from 'firebase/firestore'
import Swal from 'sweetalert2'
import { hasRevokedAccess } from '@/services/userAccess'

const menuOpen = ref(false)
const isShrunk  = ref(false)
const user      = ref(null)
const profile   = ref(null)    // ← added
const isEmployee = ref(false)  // ← check if user has employee record
const router    = useRouter()

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value
}

const closeMenu = () => {
  menuOpen.value = false
}

const handleScroll = () => {
  isShrunk.value = window.scrollY > 50
}

const handleLogout = async () => {
  try {
    await signOut(auth)
    router.push({ name: 'Login' })
  } catch (error) {
    console.error('Logout failed:', error)
    notifyError('You could not be logged out. Please try again.')
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)

  // Listen for auth changes & fetch profile
  onAuthStateChanged(auth, async (u) => {
    user.value = u
    isEmployee.value = false
    if (u) {
      // Fetch user profile from users collection (by uid first, then email)
      const usersRef = collection(db, 'users')
      let snap = await getDocs(query(usersRef, where('uid', '==', u.uid)))
      if (snap.empty) snap = await getDocs(query(usersRef, where('email', '==', u.email)))
      profile.value = snap.empty ? null : snap.docs[0].data()

      // Blocked/removed while already signed in: end the session on the next page load
      if (hasRevokedAccess(profile.value)) {
        const blocked = profile.value.accessStatus === 'blocked'
        await signOut(auth)
        router.push({ name: 'Login' })
        if (blocked) {
          Swal.fire({
            icon: 'error',
            title: 'Account Disabled',
            text: 'Your account has been disabled. Please contact RTH Group.',
            confirmButtonColor: '#1d4ed8'
          })
        }
        return
      }

      // Check if user has an employee record
      try {
        const employeesRef = collection(db, 'employees')
        const empQuery = query(employeesRef, where('email', '==', u.email))
        const empSnap = await getDocs(empQuery)
        isEmployee.value = !empSnap.empty
      } catch (err) {
        console.warn('Error checking employee status:', err)
        isEmployee.value = false
      }
    } else {
      profile.value = null
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>


<style scoped>

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background-color: #f0f6ff; /* or try #eef2f7 or #f8f5fc */
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
}
.navbar a {
  color: #333; /* Keeps text clean on light backgrounds */
  text-decoration: none;
  font-weight: 500;
  margin-left: 1.5rem;
  transition: color 0.3s ease;
}

.navbar a:hover,
.navbar .router-link-exact-active {
  color: #1e88e5; /* Blue hover and active color */
}
.logo-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.logo-container img {
  height: 50px;
  transition: height 0.3s ease;
}

.brand-text {
  font-size: 1.4rem;
  font-weight: bold;
  color: #333;
}

.menu-icon {
  display: none;
  font-size: 2rem;
  cursor: pointer;
  margin-left: auto; /* Push menu icon to the right */
}

.nav-links {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

/* MOBILE */
@media (max-width: 768px) {
  .menu-icon {
    display: block;
  }

  .nav-links {
    display: none;
    flex-direction: column;
    align-items: center;
    position: absolute;
    top: 70px;
    left: 0;
    right: 0;
    background: white;
    padding: 1rem 0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    z-index: 999;
  }

  .nav-links.active {
    display: flex;
  }

  .logo-container {
    flex: 1; /* Let it use available space */
  }

  .brand-text {
    font-size: 1.2rem;
  }
}

/* Make the “My Applications” link stand out when active */
.navbar .router-link-exact-active[href="/my-applications"] {
  font-weight: 600;
  text-decoration: underline;
}

/* Style the logout button like a tertiary link */
.logout-button {
  background: none;
  border: 1px solid #1e88e5;
  color: #1e88e5;
  padding: 0.25rem 0.6rem;
  border-radius: 4px;
  margin-left: 1rem;
  font-size: 0.9rem;
  transition: background 0.2s ease, color 0.2s ease;
}

.logout-button:hover {
  background: #1e88e5;
  color: #fff;
}

</style>
